import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, CreditCard, CheckCircle, XCircle, Clock, History, Download, Printer } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Load Razorpay script dynamically
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const FeesPage = () => {
  const { getAuthHeader, user } = useAuth();
  const [fees, setFees] = useState([]);
  const [students, setStudents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedFee, setSelectedFee] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [receiptDialog, setReceiptDialog] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [dueFeesSummary, setDueFeesSummary] = useState(null);
  const [formData, setFormData] = useState({
    student_id: '',
    amount: '',
    due_date: '',
    description: ''
  });

  const canCreateFee = ['school_admin'].includes(user?.role);
  const canPayFee = ['school_admin', 'student', 'parent'].includes(user?.role);

  useEffect(() => {
    fetchData();
    fetchPaymentConfig();
    fetchDueFeesSummary();
    // Load Razorpay script
    loadRazorpayScript().then((loaded) => {
      setRazorpayLoaded(loaded);
    });
  }, []);

  const fetchDueFeesSummary = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/fee-management/due-fees-summary`, { headers: getAuthHeader() });
      setDueFeesSummary(res.data);
    } catch (error) {
      console.error('Failed to fetch due fees summary:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [feesRes, studentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/fees`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/students`, { headers: getAuthHeader() })
      ]);
      setFees(feesRes.data);
      setStudents(studentsRes.data?.items || studentsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load fees data');
    }
  };

  const fetchPaymentConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payments/config`, { headers: getAuthHeader() });
      setPaymentConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch payment config:', error);
    }
  };

  const fetchPaymentHistory = async (feeId = null) => {
    try {
      const url = feeId 
        ? `${API_URL}/api/payments/history?fee_id=${feeId}`
        : `${API_URL}/api/payments/history`;
      const response = await axios.get(url, { headers: getAuthHeader() });
      setPaymentHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch payment history:', error);
      toast.error('Failed to load payment history');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/fees`, {
        ...formData,
        amount: parseFloat(formData.amount)
      }, { headers: getAuthHeader() });
      toast.success('Fee created successfully');
      setDialogOpen(false);
      setFormData({ student_id: '', amount: '', due_date: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create fee');
    }
  };

  const handlePayment = async (fee) => {
    if (!paymentConfig?.is_configured) {
      toast.error('Payment gateway not configured. Please contact administrator.');
      return;
    }

    if (!razorpayLoaded) {
      toast.error('Payment system is loading. Please try again.');
      return;
    }

    setSelectedFee(fee);
    setPaymentDialogOpen(true);
  };

  const initiateRazorpayPayment = useCallback(async (amount) => {
    if (!selectedFee || !window.Razorpay) return;

    setProcessingPayment(true);

    try {
      // Create order
      const orderResponse = await axios.post(
        `${API_URL}/api/payments/create-order`,
        {
          fee_id: selectedFee.id,
          amount: amount
        },
        { headers: getAuthHeader() }
      );

      const orderData = orderResponse.data;
      const student = students.find(s => s.id === selectedFee.student_id);

      // Initialize Razorpay
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Cloud Campus Schools',
        description: orderData.fee_description,
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            // Verify payment
            await axios.post(
              `${API_URL}/api/payments/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              },
              { headers: getAuthHeader() }
            );

            toast.success('Payment successful!');
            setPaymentDialogOpen(false);
            setSelectedFee(null);
            fetchData();
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
            console.error('Verification error:', error);
          }
        },
        prefill: {
          name: student?.name || '',
          email: student?.email || '',
          contact: student?.phone || ''
        },
        theme: {
          color: '#3b82f6'
        },
        modal: {
          ondismiss: () => {
            setProcessingPayment(false);
          }
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.on('payment.failed', async (response) => {
        toast.error(`Payment failed: ${response.error.description}`);
        
        // Record failed payment in database
        try {
          await axios.post(`${API_URL}/api/payments/record-failure`, {
            razorpay_order_id: response.error.metadata?.order_id,
            error_code: response.error.code,
            error_description: response.error.description,
            error_reason: response.error.reason
          }, { headers: getAuthHeader() });
        } catch (err) {
          console.error('Failed to record payment failure:', err);
        }
        
        setProcessingPayment(false);
      });
      razorpayInstance.open();

    } catch (error) {
      console.error('Payment initiation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setProcessingPayment(false);
    }
  }, [selectedFee, students, getAuthHeader]);

  // View Receipt
  const viewReceipt = async (paymentId) => {
    try {
      const res = await axios.get(`${API_URL}/api/fee-management/payment-receipt/${paymentId}`, { headers: getAuthHeader() });
      setReceiptData(res.data);
      setReceiptDialog(true);
    } catch (error) {
      toast.error('Failed to load receipt');
    }
  };

  // Download Receipt PDF
  const downloadReceiptPDF = async (paymentId, receiptNumber) => {
    try {
      toast.loading('Downloading receipt...');
      const response = await axios.get(
        `${API_URL}/api/fee-management/payment-receipt/${paymentId}/pdf`,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Receipt_${receiptNumber || paymentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.dismiss();
      toast.success('Receipt downloaded!');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to download receipt');
    }
  };

  // Print Receipt
  const printReceipt = () => {
    const printContent = document.getElementById('receipt-print-content');
    if (printContent) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Fee Receipt</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              .header { text-align: center; margin-bottom: 20px; }
              .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; }
              .total { font-weight: bold; font-size: 18px; }
              .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
            </style>
          </head>
          <body>${printContent.innerHTML}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const getTotalStats = () => {
    // Use due-fees-summary API for accurate pending amount (same as Fee Management Reports)
    if (dueFeesSummary?.summary) {
      const totalDue = dueFeesSummary.summary.total_due || 0;
      const collected = fees.reduce((sum, fee) => sum + fee.paid_amount, 0);
      const total = totalDue + collected;
      return { total, collected, pending: totalDue };
    }
    // Fallback to old calculation
    const total = fees.reduce((sum, fee) => sum + fee.amount, 0);
    const collected = fees.reduce((sum, fee) => sum + fee.paid_amount, 0);
    const pending = total - collected;
    return { total, collected, pending };
  };

  const stats = getTotalStats();

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'partial':
        return <Clock className="h-4 w-4 text-amber-600" />;
      case 'overdue':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  return (
    <DashboardLayout title="Fee Management">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="grid grid-cols-3 gap-4 w-full max-w-2xl">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Total Fees</p>
                <p className="text-2xl font-bold">₹{stats.total.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Collected</p>
                <p className="text-2xl font-bold text-green-600">₹{stats.collected.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold text-red-600">₹{stats.pending.toFixed(2)}</p>
              </CardContent>
            </Card>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                fetchPaymentHistory();
                setHistoryDialogOpen(true);
              }}
              data-testid="payment-history-btn"
            >
              <History className="h-4 w-4 mr-2" />
              Payment History
            </Button>
            {canCreateFee && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-fee-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Fee
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Fee</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="student">Student *</Label>
                      <select
                        id="student"
                        className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                        value={formData.student_id}
                        onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                        required
                      >
                        <option value="">Select student</option>
                        {students.map(student => (
                          <option key={student.id} value={student.id}>{student.roll_number || student.student_id} - {student.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="amount">Amount (₹) *</Label>
                      <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        data-testid="amount-input"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="due-date">Due Date *</Label>
                      <Input
                        id="due-date"
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                        data-testid="due-date-input"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Description *</Label>
                      <Input
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        data-testid="description-input"
                        placeholder="e.g., Tuition Fee - Semester 1"
                        required
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" data-testid="submit-fee-btn">Create Fee</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Payment Gateway Status */}
        {!paymentConfig?.is_configured && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-amber-800">
                <CreditCard className="h-5 w-5" />
                <span className="font-medium">Payment Gateway Not Configured</span>
              </div>
              <p className="text-sm text-amber-700 mt-1">
                Online payments are disabled. School Admin needs to configure Razorpay API keys in Settings → Payment Gateway.
              </p>
            </CardContent>
          </Card>
        )}

        <Card data-testid="fees-list-card">
          <CardHeader>
            <CardTitle>Fee Records ({fees.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {fees.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No fee records found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Student</th>
                      <th className="text-left py-3 px-4 font-medium">Description</th>
                      <th className="text-left py-3 px-4 font-medium">Amount</th>
                      <th className="text-left py-3 px-4 font-medium">Paid</th>
                      <th className="text-left py-3 px-4 font-medium">Status</th>
                      <th className="text-left py-3 px-4 font-medium">Due Date</th>
                      <th className="text-left py-3 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fees.map(fee => {
                      const student = students.find(s => s.id === fee.student_id);
                      const pendingAmount = fee.amount - fee.paid_amount;
                      const canPay = fee.status !== 'paid' && pendingAmount > 0;
                      
                      return (
                        <tr key={fee.id} data-testid={`fee-row-${fee.id}`} className="border-b hover:bg-muted/50 transition-colors">
                          <td className="py-3 px-4">{student?.name || 'Unknown'}</td>
                          <td className="py-3 px-4">{fee.description}</td>
                          <td className="py-3 px-4 font-medium">₹{fee.amount.toFixed(2)}</td>
                          <td className="py-3 px-4">₹{fee.paid_amount.toFixed(2)}</td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              fee.status === 'paid' ? 'bg-green-100 text-green-800' :
                              fee.status === 'partial' ? 'bg-amber-100 text-amber-800' :
                              fee.status === 'overdue' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {getStatusIcon(fee.status)}
                              {fee.status}
                            </span>
                          </td>
                          <td className="py-3 px-4">{fee.due_date}</td>
                          <td className="py-3 px-4">
                            {canPay && canPayFee && paymentConfig?.is_configured && (
                              <Button
                                size="sm"
                                onClick={() => handlePayment(fee)}
                                data-testid={`pay-fee-btn-${fee.id}`}
                              >
                                <CreditCard className="h-4 w-4 mr-1" />
                                Pay ₹{pendingAmount.toFixed(0)}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Dialog */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Pay Fee</DialogTitle>
            </DialogHeader>
            {selectedFee && (
              <div className="space-y-4">
                <div className="bg-muted p-4 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Description:</span>
                    <span className="font-medium">{selectedFee.description}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Amount:</span>
                    <span className="font-medium">₹{selectedFee.amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Already Paid:</span>
                    <span className="font-medium text-green-600">₹{selectedFee.paid_amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-muted-foreground font-medium">Pending Amount:</span>
                    <span className="font-bold text-lg">₹{(selectedFee.amount - selectedFee.paid_amount).toFixed(2)}</span>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <Button
                    onClick={() => initiateRazorpayPayment(selectedFee.amount - selectedFee.paid_amount)}
                    disabled={processingPayment}
                    className="w-full"
                    data-testid="pay-full-amount-btn"
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    {processingPayment ? 'Processing...' : `Pay Full Amount ₹${(selectedFee.amount - selectedFee.paid_amount).toFixed(0)}`}
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground text-center">
                  Secured by Razorpay. Supports UPI, Cards, Net Banking & Wallets.
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Payment History Dialog */}
        <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Payment History</DialogTitle>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto">
              {paymentHistory.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No payment history found</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 font-medium text-sm">Date</th>
                      <th className="text-left py-2 px-3 font-medium text-sm">Student</th>
                      <th className="text-left py-2 px-3 font-medium text-sm">Fee</th>
                      <th className="text-left py-2 px-3 font-medium text-sm">Amount</th>
                      <th className="text-left py-2 px-3 font-medium text-sm">Status</th>
                      <th className="text-left py-2 px-3 font-medium text-sm">Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory.map(payment => (
                      <tr key={payment.id} className="border-b text-sm">
                        <td className="py-2 px-3">
                          {new Date(payment.created_at).toLocaleDateString('en-IN')}
                        </td>
                        <td className="py-2 px-3">{payment.student_name}</td>
                        <td className="py-2 px-3">{payment.fee_description}</td>
                        <td className="py-2 px-3 font-medium">₹{payment.amount_rupees?.toFixed(2)}</td>
                        <td className="py-2 px-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            payment.status === 'paid' ? 'bg-green-100 text-green-800' :
                            payment.status === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {payment.status === 'paid' && <CheckCircle className="h-3 w-3" />}
                            {payment.status === 'failed' && <XCircle className="h-3 w-3" />}
                            {payment.status}
                          </span>
                        </td>
                        <td className="py-2 px-3">
                          {payment.status === 'paid' && (
                            <div className="flex gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => viewReceipt(payment.id)}
                                data-testid={`view-receipt-${payment.id}`}
                              >
                                <Printer className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => downloadReceiptPDF(payment.id, payment.receipt_number)}
                                data-testid={`download-receipt-${payment.id}`}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Receipt Dialog */}
        <Dialog open={receiptDialog} onOpenChange={setReceiptDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Payment Receipt</DialogTitle>
            </DialogHeader>
            {receiptData && (
              <div id="receipt-print-content" className="space-y-4 text-sm">
                <div className="text-center border-b pb-4">
                  <h2 className="font-bold text-lg">{receiptData.school?.name}</h2>
                  <p className="text-muted-foreground text-xs">{receiptData.school?.address}</p>
                  <p className="text-muted-foreground text-xs">Phone: {receiptData.school?.phone}</p>
                </div>
                
                <div className="text-center py-2">
                  <h3 className="font-bold text-lg">FEE RECEIPT</h3>
                  <p className="text-xs text-muted-foreground">Receipt No: {receiptData.receipt_number}</p>
                  <p className="text-xs text-muted-foreground">Date: {receiptData.payment_date}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-xs border-y py-3">
                  <div>
                    <p className="text-muted-foreground">Student Name</p>
                    <p className="font-medium">{receiptData.student?.name}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Class</p>
                    <p className="font-medium">{receiptData.student?.class_name}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Student ID</p>
                    <p className="font-medium">{receiptData.student?.student_id}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Father&apos;s Name</p>
                    <p className="font-medium">{receiptData.student?.father_name || '-'}</p>
                  </div>
                </div>
                
                <div className="border-b pb-3">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1">Fee Description</th>
                        <th className="text-right py-1">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receiptData.invoices?.map((inv, i) => (
                        <tr key={i}>
                          <td className="py-1">{inv.fee_head_name || inv.description}</td>
                          <td className="text-right py-1">₹{inv.amount?.toLocaleString()}</td>
                        </tr>
                      )) || (
                        <tr>
                          <td className="py-1">{receiptData.fee_description || 'Fee Payment'}</td>
                          <td className="text-right py-1">₹{receiptData.amount_paid?.toLocaleString()}</td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="border-t font-bold">
                        <td className="py-2">Total Paid</td>
                        <td className="text-right py-2 text-green-600">₹{receiptData.amount_paid?.toLocaleString()}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                
                <div className="text-xs space-y-1">
                  <p>Payment Mode: {receiptData.payment_mode || 'Online'}</p>
                  {receiptData.transaction_id && <p>Transaction ID: {receiptData.transaction_id}</p>}
                  <p>Received By: {receiptData.collected_by || 'Online Payment'}</p>
                </div>
                
                <div className="text-center text-xs text-muted-foreground border-t pt-3">
                  <p>This is a computer-generated receipt.</p>
                  <p>No signature required.</p>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setReceiptDialog(false)}>Close</Button>
              <Button onClick={printReceipt}>
                <Printer className="h-4 w-4 mr-2" /> Print
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default FeesPage;
